import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import {
  ENCOUNTER_TYPES,
  Encounter,
  EncounterLookupResponse,
  EncounterChecklistItem,
  MarkNonBillablePayload,
  NonBillableReason,
  isEncounterTypeValid
} from '@/types/encounter';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';

export const encountersRepository = {
  async getEncounterByHealthieId({
    host,
    encounterType,
    healthieId
  }: {
    host: string;
    encounterType: ENCOUNTER_TYPES;
    healthieId: string;
  }): Promise<RepositoryResponse<EncounterLookupResponse>> {
    try {
      const path = `/api/dugout/encounters/${encounterType}/by_healthie_id/${healthieId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });
      return {
        data: response.data as EncounterLookupResponse,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(
        error as AxiosError,
        'Failed to get encounters by healthie id.'
      );
    }
  },
  async getEncounterById({
    host,
    encounterType,
    encounterId
  }: {
    host: string;
    encounterType: ENCOUNTER_TYPES;
    encounterId: string;
  }): Promise<RepositoryResponse<Encounter>> {
    try {
      const path = `/api/dugout/encounters/${encounterType}/${encounterId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });
      return {
        data: response.data as Encounter,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(
        error as AxiosError,
        'Failed to get encounters by encounter id.'
      );
    }
  },
  async getAttendanceChecklist({
    host,
    encounterId
  }: {
    host: string;
    encounterId: string;
  }): Promise<RepositoryResponse<EncounterChecklistItem[]>> {
    try {
      const path = `/api/dugout/encounters/${ENCOUNTER_TYPES.APPOINTMENT_ATTENDANCE}/${encounterId}/checklist/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });
      return {
        data: response.data as EncounterChecklistItem[],
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get encounter checklist.');
    }
  },
  async refreshAttendanceChecklist({
    host,
    encounterId
  }: {
    host: string;
    encounterId: string;
  }): Promise<RepositoryResponse<EncounterChecklistItem[]>> {
    try {
      const path = `/api/dugout/encounters/${ENCOUNTER_TYPES.APPOINTMENT_ATTENDANCE}/${encounterId}/checklist/refresh/`;
      const response = await ajax.post({ url: `${host}${path}`, data: {} as never });
      return {
        data: response.data as EncounterChecklistItem[],
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to refresh encounter checklist.');
    }
  },
  async getNonBillableReasons({
    host
  }: {
    host: string;
  }): Promise<RepositoryResponse<NonBillableReason[]>> {
    try {
      const path = `/api/dugout/non_billable_reasons/?active=true`;
      const response = await ajax.get({
        url: `${host}${path}`
      });
      return {
        data: response.data as NonBillableReason[],
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get non billable reasons.');
    }
  },
  async markItemAsNonBillable({
    host,
    encounterType,
    encounterId,
    nonBillableReason
  }: {
    host: string;
    encounterType: ENCOUNTER_TYPES;
    encounterId: string;
    nonBillableReason: MarkNonBillablePayload;
  }): Promise<RepositoryResponse<Encounter>> {
    try {
      const path = `/api/dugout/encounters/${encounterType}/${encounterId}/mark_as_non_billable/`;
      const response = await ajax.patch<MarkNonBillablePayload, AxiosResponse<Encounter>>({
        url: `${host}${path}`,
        data: nonBillableReason
      });
      return {
        data: response.data as Encounter,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to mark item as non billable.');
    }
  },
  async markItemAsBillable({
    host,
    encounterType,
    encounterId
  }: {
    host: string;
    encounterType: ENCOUNTER_TYPES;
    encounterId: string;
  }): Promise<RepositoryResponse<Encounter>> {
    try {
      const path = `/api/dugout/encounters/${encounterType}/${encounterId}/mark_as_billable/`;
      const response = await ajax.patch({
        url: `${host}${path}`
      });

      return {
        data: response.data as Encounter,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to mark item as billable.');
    }
  }
};

const EncountersRepository = () => {
  const { host } = useContext(AppContext);

  const isSnakeCase = (inputString: string) => /^[a-z_][a-z0-9_]*$/.test(inputString);

  return {
    async getEncounterByHealthieId({
      encounterType,
      healthieId
    }: {
      encounterType: string;
      healthieId: string;
    }): Promise<RepositoryResponse<EncounterLookupResponse>> {
      const encounterTypePath = isSnakeCase(encounterType)
        ? encounterType
        : encounterType?.replace(/-/g, '_');

      if (!isEncounterTypeValid(encounterTypePath))
        throw new Error(`Encounter type: ${encounterType} is not valid`);

      return encountersRepository.getEncounterByHealthieId({
        host,
        encounterType: encounterTypePath as ENCOUNTER_TYPES,
        healthieId
      });
    },
    async getEncounterById({
      encounterType,
      encounterId
    }: {
      encounterType: string;
      encounterId: string;
    }): Promise<RepositoryResponse<Encounter>> {
      if (!isEncounterTypeValid(encounterType))
        throw new Error(`Encounter type: ${encounterType} is not valid`);
      return encountersRepository.getEncounterById({
        host,
        encounterType: encounterType as ENCOUNTER_TYPES,
        encounterId
      });
    },
    async getAttendanceChecklist({
      encounterId
    }: {
      encounterId: string;
    }): Promise<RepositoryResponse<EncounterChecklistItem[]>> {
      return encountersRepository.getAttendanceChecklist({ host, encounterId });
    },
    async refreshAttendanceChecklist({
      encounterId
    }: {
      encounterId: string;
    }): Promise<RepositoryResponse<EncounterChecklistItem[]>> {
      return encountersRepository.refreshAttendanceChecklist({ host, encounterId });
    },
    async getNonBillableReasons(): Promise<RepositoryResponse<NonBillableReason[]>> {
      return encountersRepository.getNonBillableReasons({ host });
    },
    async markItemAsNonBillable({
      encounterType,
      encounterId,
      nonBillableReason
    }: {
      encounterType: ENCOUNTER_TYPES;
      encounterId: string;
      nonBillableReason: MarkNonBillablePayload;
    }): Promise<RepositoryResponse<Encounter>> {
      return encountersRepository.markItemAsNonBillable({
        host,
        encounterType,
        encounterId,
        nonBillableReason
      });
    },
    async markItemAsBillable({
      encounterType,
      encounterId
    }: {
      encounterType: ENCOUNTER_TYPES;
      encounterId: string;
    }): Promise<RepositoryResponse<Encounter>> {
      return encountersRepository.markItemAsBillable({
        host,
        encounterType,
        encounterId
      });
    }
  };
};

export default EncountersRepository;
